import React from 'react'
import { useNavigate } from 'react-router'
import { CreditApplication } from '_gql'

import { useMakeTestId } from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_DANGER, useToast } from '@divvy-web/skylab.toast'

import { useAuth } from '@sputnik/auth'
import { StatusButtons } from '@sputnik/components'
import ImageWrapper from '@sputnik/components/ImageWrapper/ImageWrapper'
import StatusTile from '@sputnik/pages/Dashboard/StatusTile/StatusTile'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import { useCloneCreditApplication } from './CloneCreditApplication.gql'

interface RefreshApplicationStatusTileProps {
  creditApp: CreditApplication
}

// this tile shows for statuses: CLOSED_LOST, EXPIRED, CLOSED_AS_DUPLICATE, CUSTOMER_CANCELED
const RefreshApplicationStatusTile = ({ creditApp }: RefreshApplicationStatusTileProps) => {
  const makeTestId = useMakeTestId('RefreshApplicationStatusTile')
  const { salesforceCreditId, lastUpdatedByApplicantAt } = creditApp || {}
  const navigate = useNavigate()
  const { setToken } = useAuth()
  const [cloneCreditApp, { loading: isCloningApplication }] = useCloneCreditApplication()
  const showDangerToast = useToast(TOAST_TYPE_DANGER) as any

  const onRefreshApplicationClick = () => {
    cloneCreditApp({ variables: { salesforceCreditId } })
      .then((result) => {
        const newToken = result?.data?.cloneCreditApplication?.token ?? ''
        setToken(newToken)
        const newCreditId = result?.data?.cloneCreditApplication?.salesforceCreditId

        logInfo('cloneCreditApplication', {
          attributes: {
            originalCreditAppId: salesforceCreditId,
            newCreditAppId: newCreditId,
            result: 'Successfully cloned a credit application',
          },
          eventName: 'CloneCreditApplication',
          message: `Original Credit App ID: ${salesforceCreditId}, new Credit App Id: ${newCreditId} `,
        })
        navigate(`/app/${newCreditId}/${PATHNAME_CONSTS.NAME_AND_OWNERSHIP_PATH}`, {
          state: { autoRedirectIndex: 0 },
        })
      })
      .catch((e) => {
        logError(e, {
          attributes: {
            action: 'cloneCreditApplication',
            creditApplicationId: salesforceCreditId,
            result: 'Error occured when cloning credit application',
          },
          eventName: 'CloneCreditApplication',
        })
        showDangerToast(
          <FormattedMessage
            defaultMessage='Error cloning credit application.'
            id='sputnik.RefreshApplicationStatusTile__KnZAEc'
          />,
          {
            autoHideDelay: 5000,
            dataTestId: 'clone-application-fail',
          },
        )
      })
  }
  return (
    <div data-testid={makeTestId('')}>
      <StatusTile
        creditApp={creditApp}
        image={
          <ImageWrapper
            alt='two arrows circling each other'
            imageName='dashboard-application-refresh-status'
          />
        }
        primaryInfo={
          <FormattedMessage
            defaultMessage='Even though your application expired, we have your information saved. Refresh your application now to pick up where you left off. '
            id='sputnik.RefreshApplicationStatusTile__e8AIFW'
          />
        }
        secondaryInfo={
          <FormattedMessage
            defaultMessage='Application submitted on {date, date, long}'
            id='sputnik.RefreshApplicationStatusTile__7CIzhR'
            values={{ date: new Date(lastUpdatedByApplicantAt) }}
          />
        }
        title={
          <FormattedMessage
            defaultMessage='Application refresh'
            id='sputnik.RefreshApplicationStatusTile__NoChSM'
          />
        }
      >
        <StatusButtons
          hideSecondary
          primaryLoading={isCloningApplication}
          primaryText={
            <FormattedMessage
              defaultMessage='Refresh my application'
              id='sputnik.RefreshApplicationStatusTile__fCH3Ai'
            />
          }
          onPrimaryClick={onRefreshApplicationClick}
        />
      </StatusTile>
    </div>
  )
}

export default RefreshApplicationStatusTile
